import React, { useState, useEffect } from 'react';
import { subscribeToCollection, saveDocument, deleteDocument } from '../firebase';
import { getUserName, isSameUser } from '../utils/userUtils';

import { DAYS, dateToTripDay, parseTimeAndDate } from '../utils/dateUtils';

const EMOJIS = ["☀️", "🏔️", "🍽️", "🌊", "🧗", "🧘", "🚗", "🍷", "🍳", "🏡", "🎒", "🥩", "🥞", "🛒", "🔥", "🚲", "🍿", "🏓"];

const formatPassengersList = (names) => {
  if (!names || names.length === 0) return "Guest";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} & ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]}`;
};

export default function Schedule({ users = [] }) {
  const [scheduleItems, setScheduleItems] = useState([]);
  const [activities, setActivities] = useState([]);
  const [itineraries, setItineraries] = useState([]);
  const [legs, setLegs] = useState([]);
  const [dinners, setDinners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeDay, setActiveDay] = useState("Saturday 6/13");
  const [showAddForm, setShowAddForm] = useState(false);

  // Form Mode: "freeform" or "activity"
  const [formMode, setFormMode] = useState("freeform");

  // Form Fields
  const [selectedActivityId, setSelectedActivityId] = useState("");
  const [time, setTime] = useState("");
  const [title, setTitle] = useState("");
  const [emoji, setEmoji] = useState("📅");
  const [notes, setNotes] = useState("");
  const [day, setDay] = useState(activeDay);

  // Edit States
  const [editingItemId, setEditingItemId] = useState(null);
  const [editDay, setEditDay] = useState("Saturday 6/13");
  const [editTime, setEditTime] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [editEmoji, setEditEmoji] = useState("");
  const [editNotes, setEditNotes] = useState("");

  const loggedInUserId = localStorage.getItem("prine_user_name") || "";

  useEffect(() => {
    setIsLoading(true);
    // Subscribe to Schedule collection
    const unsubscribeSchedule = subscribeToCollection("schedule", (data) => {
      setScheduleItems(data);
      setIsLoading(false);
    });

    // Subscribe to Activities to allow linking them
    const unsubscribeActivities = subscribeToCollection("activities", (data) => {
      setActivities(data);
    });

    // Subscribe to Flight itineraries
    const unsubscribeItineraries = subscribeToCollection("itineraries_v3", (data) => {
      setItineraries(data);
    });

    // Subscribe to Flight legs
    const unsubscribeLegs = subscribeToCollection("legs_v3", (data) => {
      setLegs(data);
    });

    // Subscribe to Dinners
    const unsubscribeDinners = subscribeToCollection("dinners", (data) => {
      setDinners(data);
    });

    return () => {
      unsubscribeSchedule();
      unsubscribeActivities();
      unsubscribeItineraries();
      unsubscribeLegs();
      unsubscribeDinners();
    };
  }, []);

  // Listen to deep-link hash redirection (e.g. #schedule?activity=act_123)
  useEffect(() => {
    const checkHashParams = () => {
      const hash = window.location.hash;
      if (hash.includes("schedule")) {
        const match = hash.match(/activity=([^&]+)/);
        if (match) {
          const actId = match[1];
          setSelectedActivityId(actId);
          setFormMode("activity");
          setShowAddForm(true);

          // Find the activity if loaded, and pre-populate title & emoji
          const act = activities.find(a => a.id === actId);
          if (act) {
            setTitle(act.title || "");
            setEmoji(act.emoji || "🏔️");
          }

          // Clear query params cleanly from the browser URL address bar
          window.history.replaceState(null, "", "#schedule");
        }
      }
    };

    if (activities.length > 0) {
      checkHashParams();
    }
  }, [activities]);

  // When selected activity changes, auto-fill title & emoji only (notes are sourced from the live activity)
  const handleSelectActivity = (activityId) => {
    setSelectedActivityId(activityId);
    if (!activityId) {
      setTitle("");
      setEmoji("🏔️");
      return;
    }
    const act = activities.find(a => a.id === activityId);
    if (act) {
      setTitle(act.title || "");
      setEmoji(act.emoji || "🏔️");
      // Do NOT pre-fill notes — the activity is the single source of truth
    }
  };

  const handleAddEvent = async (e) => {
    e.preventDefault();
    const isLinkedActivity = formMode === "activity";
    if (!isLinkedActivity && !title.trim()) {
      alert("Event Title is required!");
      return;
    }
    if (isLinkedActivity && !selectedActivityId) {
      alert("Please choose a Nominated Activity to link.");
      return;
    }
    if (!time.trim()) {
      alert("Time is required!");
      return;
    }

    const newItem = {
      id: `sched_${Date.now()}`,
      day,
      time: time.trim(),
      // For linked activities: title/emoji/notes are sourced live from the activity
      title: isLinkedActivity ? "" : title.trim(),
      emoji: isLinkedActivity ? "" : emoji.trim(),
      notes: "", // notes are never stored for any event (activities use live data; freeform had its own)
      activityId: isLinkedActivity ? selectedActivityId : "",
      createdBy: loggedInUserId
    };

    // For freeform, we do store the notes
    if (!isLinkedActivity) {
      newItem.notes = notes.trim();
    }

    await saveDocument("schedule", newItem.id, newItem);

    // Reset Form
    setTitle("");
    setTime("");
    setNotes("");
    setEmoji("📅");
    setSelectedActivityId("");
    setShowAddForm(false);
  };

  const handleStartEdit = (item) => {
    setEditingItemId(item.id);
    setEditDay(item.day || "Saturday 6/13");
    setEditTime(item.time || "");
    setEditTitle(item.title || "");
    setEditEmoji(item.emoji || "📅");
    setEditNotes(item.notes || "");
  };

  const handleSaveEdit = async (item) => {
    const isLinkedActivity = !!item.activityId;
    if (!isLinkedActivity && !editTitle.trim()) {
      alert("Title is required!");
      return;
    }
    if (!editTime.trim()) {
      alert("Time is required!");
      return;
    }

    const updated = {
      ...item,
      day: editDay,
      time: editTime.trim(),
      // For linked activities, title/emoji/notes are never stored — sourced live from the activity
      ...(isLinkedActivity ? {} : {
        title: editTitle.trim(),
        emoji: editEmoji.trim(),
        notes: editNotes.trim()
      })
    };

    await saveDocument("schedule", item.id, updated);
    setEditingItemId(null);
  };

  const handleDeleteEvent = async (itemId) => {
    if (!window.confirm("Are you sure you want to remove this event from the schedule?")) return;
    await deleteDocument("schedule", itemId);
  };

  // Cron/Chronological sorting helper scores
  const timeToSortScore = (timeStr) => {
    if (!timeStr) return 9999;
    const normalized = timeStr.trim().toLowerCase();

    if (normalized.includes("all day") || normalized.includes("morning")) return 0;
    if (normalized.includes("noon")) return 12 * 60;
    if (normalized.includes("afternoon")) return 13 * 60;
    if (normalized.includes("evening") || normalized.includes("sunset")) return 18 * 60;
    if (normalized.includes("night") || normalized.includes("dinner")) return 20 * 60;

    const match = normalized.match(/(\d+)(?::(\d+))?\s*(am|pm)?/);
    if (match) {
      let hours = parseInt(match[1], 10);
      const minutes = match[2] ? parseInt(match[2], 10) : 0;
      const ampm = match[3];

      if (ampm === "pm" && hours < 12) hours += 12;
      if (ampm === "am" && hours === 12) hours = 0;

      return hours * 60 + minutes;
    }
    return 9999;
  };

  // Dynamically map flights to schedule items (Arrivals for incoming, departures for outgoing!)
  const flightScheduleItems = [];
  itineraries.forEach(itinerary => {
    const itineraryLegs = legs.filter(l => l.itineraryId === itinerary.id);
    if (itineraryLegs.length === 0) return;

    // Sort legs chronologically by departure time
    const getLegDepartureTimeMs = (leg) => {
      const depVal = leg.departure?.scheduled || leg.departure?.estimated || leg.date || "";
      if (!depVal) return 0;
      const parsed = Date.parse(depVal);
      return isNaN(parsed) ? 0 : parsed;
    };

    const sortedLegs = [...itineraryLegs].sort((a, b) => {
      return getLegDepartureTimeMs(a) - getLegDepartureTimeMs(b);
    });

    const isIncoming = itinerary.direction === "incoming";
    // Only show the final leg of an incoming itinerary and the first leg of an outgoing itinerary
    const targetLeg = isIncoming ? sortedLegs[sortedLegs.length - 1] : sortedLegs[0];

    const rawDepTime = targetLeg.departure?.scheduled || targetLeg.departure?.estimated || "";
    const rawArrTime = targetLeg.arrival?.scheduled || targetLeg.arrival?.estimated || "";

    const depParsed = parseTimeAndDate(rawDepTime || targetLeg.date);
    const arrParsed = parseTimeAndDate(rawArrTime);

    const depTime = depParsed.time;
    const arrTime = arrParsed.time;

    const depDate = depParsed.date || targetLeg.date;
    const arrDate = arrParsed.date || targetLeg.date;

    const targetDate = isIncoming ? arrDate : depDate;
    const tripDay = dateToTripDay(targetDate);

    if (!tripDay) return; // Skip if date is out of vacation range

    const passengerNames = Array.isArray(itinerary.passengers)
      ? itinerary.passengers.map(p => getUserName(users, p))
      : [];

    const depAirport = targetLeg.departureAirport || targetLeg.departure?.iata || "TBD";
    const arrAirport = targetLeg.arrival?.iata || targetLeg.arrival?.airport || "TBD";
    const flightNum = `${targetLeg.airlineCode}${targetLeg.flightNumber}`;

    const passengersStr = formatPassengersList(passengerNames);

    const flightStatusLabel = targetLeg.flight_status
      ? targetLeg.flight_status.charAt(0).toUpperCase() + targetLeg.flight_status.slice(1).toLowerCase()
      : "Scheduled";

    if (isIncoming) {
      // Incoming flight = ARRIVAL at destination
      flightScheduleItems.push({
        id: `flight_arr_${targetLeg.id}`,
        day: tripDay,
        time: arrTime || "TBD",
        title: `Arrival: ${passengersStr} (${arrAirport})`,
        emoji: "🛬",
        notes: `Flight ${flightNum} from ${depAirport} (${flightStatusLabel})`,
        createdBy: "System",
        isFlight: true,
        isArrival: true,
        flightyUrl: targetLeg.flightyUrl,
        itineraryId: itinerary.id
      });
    } else {
      // Outgoing flight = DEPARTURE from origin
      flightScheduleItems.push({
        id: `flight_dep_${targetLeg.id}`,
        day: tripDay,
        time: depTime || "TBD",
        title: `Departure: ${passengersStr} (${depAirport})`,
        emoji: "🛫",
        notes: `Flight ${flightNum} to ${arrAirport} (${flightStatusLabel})`,
        createdBy: "System",
        isFlight: true,
        isArrival: false,
        flightyUrl: targetLeg.flightyUrl,
        itineraryId: itinerary.id
      });
    }
  });

  // Dinner virtual schedule items — one per trip day (Sat 6/13 – Fri 6/19)
  const DINNER_DAYS = [
    "Saturday 6/13", "Sunday 6/14", "Monday 6/15", "Tuesday 6/16",
    "Wednesday 6/17", "Thursday 6/18", "Friday 6/19"
  ];
  const dinnerScheduleItems = DINNER_DAYS.map(dinnerDay => {
    const dinnerDoc = dinners.find(d => d.day === dinnerDay);
    return {
      id: `dinner_${dinnerDay.replace(/[^a-z0-9]/gi, '_')}`,
      day: dinnerDay,
      time: "Dinner",
      title: (dinnerDoc && dinnerDoc.theme) ? dinnerDoc.theme : "Dinner (TBD)",
      emoji: "🍽️",
      notes: (dinnerDoc && dinnerDoc.menu) ? dinnerDoc.menu : "",
      createdBy: "System",
      isDinner: true,
      dinnerFirestoreId: dinnerDoc ? dinnerDoc.id : "",
      chef: (dinnerDoc && dinnerDoc.chef) ? dinnerDoc.chef : "",
      washer: (dinnerDoc && dinnerDoc.washer) ? dinnerDoc.washer : ""
    };
  });

  // Filter & sort active day items
  const userItems = scheduleItems.filter(item => item.day === activeDay);
  const flightItems = flightScheduleItems.filter(item => item.day === activeDay);
  const dinnerItems = dinnerScheduleItems.filter(item => item.day === activeDay);

  const allDayItems = [...userItems, ...flightItems, ...dinnerItems].sort((a, b) => timeToSortScore(a.time) - timeToSortScore(b.time));

  // Count items per day for the visual badges
  const getCountForDay = (dayName) => {
    const userCount = scheduleItems.filter(item => item.day === dayName).length;
    const flightCount = flightScheduleItems.filter(item => item.day === dayName).length;
    const dinnerCount = dinnerScheduleItems.filter(item => item.day === dayName).length;
    return userCount + flightCount + dinnerCount;
  };

  return (
    <div className="tab-panel">
      {/* Schedule Custom Timeline Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
        .schedule-timeline-container {
          position: relative;
          padding-left: 32px;
          margin-top: 24px;
        }
        .schedule-timeline-line {
          position: absolute;
          left: 11px;
          top: 8px;
          bottom: 8px;
          width: 2px;
          background: linear-gradient(to bottom, var(--color-primary), var(--color-secondary), rgba(255, 255, 255, 0.05));
        }
        .schedule-day-tabs-wrapper {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          padding: 8px 0;
          margin-bottom: 24px;
          scroll-behavior: smooth;
          -webkit-overflow-scrolling: touch;
        }
        .schedule-day-tabs-wrapper::-webkit-scrollbar {
          height: 4px;
        }
        .schedule-day-tabs-wrapper::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 2px;
        }
        .schedule-day-tab {
          flex: 0 0 auto;
          padding: 8px 16px;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.06);
          border-radius: 20px;
          color: var(--color-text-muted);
          font-weight: 600;
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 6px;
          user-select: none;
        }
        .schedule-day-tab:hover {
          background: rgba(255, 255, 255, 0.06);
          border-color: var(--color-primary);
          color: var(--color-text-main);
        }
        .schedule-day-tab.active {
          background: rgba(2, 132, 199, 0.12);
          border-color: var(--color-primary);
          color: var(--color-text-main);
          box-shadow: 0 0 12px rgba(2, 132, 199, 0.2);
        }
        .schedule-badge {
          font-size: 0.7rem;
          padding: 1px 6px;
          border-radius: 10px;
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: var(--color-text-muted);
          font-weight: 700;
        }
        .schedule-day-tab.active .schedule-badge {
          background: var(--color-primary);
          border-color: transparent;
          color: #ffffff !important;
        }
        :root[data-theme="light"] .schedule-day-tab:not(.active) .schedule-badge {
          background: rgba(15, 23, 42, 0.05) !important;
          border: 1px solid rgba(15, 23, 42, 0.12) !important;
          color: #0f172a !important;
        }
        .timeline-item {
          position: relative;
          margin-bottom: 28px;
        }
        .timeline-marker {
          position: absolute;
          left: -32px;
          top: 6px;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: var(--color-bg-dark, #0b0f19);
          border: 3px solid var(--color-primary);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.7rem;
          z-index: 2;
          box-shadow: 0 0 8px rgba(2, 132, 199, 0.4);
          transition: all 0.3s ease;
        }
        .timeline-item:hover .timeline-marker {
          border-color: var(--color-secondary);
          box-shadow: 0 0 10px rgba(245, 158, 11, 0.5);
          transform: scale(1.1);
        }
        .timeline-card {
          margin-left: 0;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .timeline-card:hover {
          transform: translateX(4px);
        }
      `
      }} />

      <div className="view-title-container">
        <div className="view-title">
          <h2>🗓️ Schedule</h2>
        </div>
        <button className="btn-primary" onClick={() => { if (!showAddForm) setDay(activeDay); setShowAddForm(!showAddForm); }}>
          {showAddForm ? "Cancel Event" : "+ Add Event"}
        </button>
      </div>

      {showAddForm && (
        <form onSubmit={handleAddEvent} className="glass-panel" style={{ marginBottom: '32px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <h3 style={{ fontFamily: 'var(--font-title)', fontSize: '1.25rem', color: 'var(--color-text-main)', margin: 0 }}>Schedule an Event</h3>

          <div style={{ display: 'flex', gap: '16px', borderBottom: '1px solid var(--color-glass-border)', paddingBottom: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', cursor: 'pointer', fontWeight: '600' }}>
              <input
                type="radio"
                name="formMode"
                checked={formMode === "freeform"}
                onChange={() => { setFormMode("freeform"); setSelectedActivityId(""); setTitle(""); }}
              />
              Freeform Event
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', cursor: 'pointer', fontWeight: '600' }}>
              <input
                type="radio"
                name="formMode"
                checked={formMode === "activity"}
                onChange={() => setFormMode("activity")}
              />
              Link Nominated Activity
            </label>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Day of Trip</label>
              <select className="input-field" value={day} onChange={e => setDay(e.target.value)} required>
                {DAYS.map(d => (
                  <option key={d} value={d} style={{ background: '#1e293b' }}>{d}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Time / Window (e.g. 10:00 AM, Sunset)</label>
              <input
                type="text"
                placeholder="e.g. 10:00 AM or All Day"
                className="input-field"
                value={time}
                onChange={e => setTime(e.target.value)}
                required
              />
            </div>

            {formMode === "activity" ? (
              <div className="form-group">
                <label>Choose Live Activity</label>
                <select
                  className="input-field"
                  value={selectedActivityId}
                  onChange={e => handleSelectActivity(e.target.value)}
                  required
                >
                  <option value="" style={{ background: '#1e293b', color: '#94a3b8' }}>-- Choose Activity --</option>
                  {activities.map(a => (
                    <option key={a.id} value={a.id} style={{ background: '#1e293b' }}>
                      {a.emoji || "🏔️"} {a.title} (❤️ {a.votes || 0})
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="form-group" style={{ display: 'flex', gap: '8px' }}>
                <div style={{ flex: 1 }}>
                  <label>Event Title</label>
                  <input
                    type="text"
                    placeholder="e.g. Morning Family Walk"
                    className="input-field"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    required
                  />
                </div>
                <div style={{ width: '80px' }}>
                  <label>Emoji</label>
                  <select
                    className="input-field"
                    value={emoji}
                    onChange={e => setEmoji(e.target.value)}
                  >
                    {EMOJIS.map(em => (
                      <option key={em} value={em} style={{ background: '#1e293b' }}>{em}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          {formMode !== "activity" && (
            <div className="form-group">
              <label>Scheduling Notes / Directives (e.g. Who is driving, what to bring)</label>
              <textarea
                placeholder="e.g. Pack swimming gears and water bottles. Meeting in the lodge lobby."
                className="input-field"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows="3"
              />
            </div>
          )}

          <button type="submit" className="btn-primary" style={{ alignSelf: 'flex-start' }}>Schedule Event</button>
        </form>
      )}

      {/* Day Swapping Navigation Bar */}
      <div className="schedule-day-tabs-wrapper">
        {DAYS.map(d => {
          const count = getCountForDay(d);
          return (
            <div
              key={d}
              className={`schedule-day-tab ${activeDay === d ? 'active' : ''}`}
              onClick={() => setActiveDay(d)}
            >
              <span>{d}</span>
              {count > 0 && <span className="schedule-badge">{count}</span>}
            </div>
          );
        })}
      </div>

      {isLoading ? (
        <div className="loading-container">
          <div className="loading-chip">
            <span className="spinner"></span>
            <span>Syncing Schedule Timeline...</span>
          </div>
        </div>
      ) : allDayItems.length === 0 ? (
        <div className="glass-panel" style={{ padding: '48px', textAlign: 'center', color: 'var(--color-text-muted)', width: '100%' }}>
          No events scheduled for {activeDay} yet. Add an event to the day's itinerary!
        </div>
      ) : (
        <div className="schedule-timeline-container">
          <div className="schedule-timeline-line"></div>

          {allDayItems.map((item, index) => {
            const isEditing = editingItemId === item.id;
            const isOwner = isSameUser(users, item.createdBy, loggedInUserId);

            // Check if it is a linked activity
            const linkedActivity = item.activityId ? activities.find(a => a.id === item.activityId) : null;

            if (isEditing) {
              const isLinkedEdit = !!item.activityId;
              return (
                <div key={item.id} className="timeline-item">
                  <div className="timeline-marker">✏️</div>
                  <div className="glass-panel timeline-card" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <h4 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--color-text-main)' }}>Edit Scheduled Event</h4>
                    {/* Day + Time row — always shown */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div className="form-group">
                        <label style={{ fontSize: '0.8rem' }}>Day of Trip</label>
                        <select
                          className="input-field"
                          value={editDay}
                          onChange={e => setEditDay(e.target.value)}
                          style={{ fontSize: '0.85rem', width: '100%' }}
                        >
                          {DAYS.map(d => (
                            <option key={d} value={d} style={{ background: '#1e293b' }}>{d}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label style={{ fontSize: '0.8rem' }}>Time / Window</label>
                        <input
                          type="text"
                          className="input-field"
                          value={editTime}
                          onChange={e => setEditTime(e.target.value)}
                          style={{ fontSize: '0.85rem', width: '100%' }}
                        />
                      </div>
                    </div>
                    {/* Title / Emoji / Notes — only editable by owner of a freeform item */}
                    {(() => {
                      const canEditContent = isOwner && !isLinkedEdit;
                      if (!canEditContent) return (
                        <p style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', margin: 0, fontStyle: 'italic' }}>
                          {isLinkedEdit
                            ? 'Title, emoji, and notes are sourced from the linked Nominated Activity.'
                            : 'Only the event creator can edit the title and notes.'}
                        </p>
                      );
                      return (
                        <>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <div className="form-group" style={{ flex: 1, minWidth: '160px' }}>
                              <label style={{ fontSize: '0.8rem' }}>Title</label>
                              <input
                                type="text"
                                className="input-field"
                                value={editTitle}
                                onChange={e => setEditTitle(e.target.value)}
                                style={{ fontSize: '0.85rem', width: '100%' }}
                              />
                            </div>
                            <div className="form-group" style={{ width: '80px' }}>
                              <label style={{ fontSize: '0.8rem' }}>Emoji</label>
                              <input
                                type="text"
                                className="input-field"
                                value={editEmoji}
                                onChange={e => setEditEmoji(e.target.value)}
                                style={{ fontSize: '0.85rem', textAlign: 'center', width: '100%' }}
                              />
                            </div>
                          </div>
                          <div className="form-group">
                            <label style={{ fontSize: '0.8rem' }}>Notes</label>
                            <textarea
                              className="input-field"
                              value={editNotes}
                              onChange={e => setEditNotes(e.target.value)}
                              rows="2"
                              style={{ fontSize: '0.85rem', width: '100%' }}
                            />
                          </div>
                        </>
                      );
                    })()}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                      <button
                        className="btn-primary"
                        onClick={() => handleSaveEdit(item)}
                        style={{ fontSize: '0.75rem', padding: '6px 14px' }}
                      >
                        Save
                      </button>
                      <button
                        className="btn-secondary"
                        onClick={() => setEditingItemId(null)}
                        style={{ fontSize: '0.75rem', padding: '6px 14px' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              );
            }

            if (item.isFlight) {
              const accentColor = item.isArrival ? 'var(--color-accent)' : 'var(--color-secondary)';
              const markerEmoji = item.isArrival ? '🛬' : '🛫';

              return (
                <div key={item.id} className="timeline-item">
                  <div className="timeline-marker" style={{ fontSize: '0.8rem', borderColor: accentColor }}>
                    {markerEmoji}
                  </div>
                  <div className="glass-panel timeline-card" style={{ padding: '12px 18px', display: 'flex', flexDirection: 'column', gap: '6px', borderLeft: `3px solid ${accentColor}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: '0.75rem',
                        fontWeight: '700',
                        color: accentColor,
                        background: item.isArrival ? 'rgba(16, 185, 129, 0.08)' : 'rgba(245, 158, 11, 0.08)',
                        padding: '2px 8px',
                        borderRadius: '20px',
                        border: `1px solid ${item.isArrival ? 'rgba(16, 185, 129, 0.15)' : 'rgba(245, 158, 11, 0.15)'}`
                      }}>
                        {item.time}
                      </span>
                      <h3 style={{ margin: 0, fontSize: '1.05rem', fontFamily: 'var(--font-title)', fontWeight: '600' }}>
                        {item.title}
                      </h3>
                    </div>
                    <p style={{
                      fontSize: '0.8rem',
                      color: 'var(--color-text-muted)',
                      margin: '2px 0 4px 0',
                      wordBreak: 'normal',
                      overflowWrap: 'break-word'
                    }}>
                      {item.notes}
                    </p>
                    {/* Flight card footer links */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderTop: '1px solid var(--color-glass-border)',
                      paddingTop: '8px',
                      marginTop: '2px',
                      flexWrap: 'wrap',
                      gap: '8px'
                    }}>
                      {item.flightyUrl ? (
                        <a
                          href={item.flightyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            fontSize: '0.75rem',
                            color: accentColor,
                            fontWeight: '600',
                            textDecoration: 'none',
                          }}
                        >
                          View on Flighty ↗️
                        </a>
                      ) : <span />}
                      <a
                        href="#flights"
                        onClick={() => {
                          if (item.itineraryId) {
                            sessionStorage.setItem('prine_focus_itinerary', item.itineraryId);
                          }
                        }}
                        style={{
                          fontSize: '0.75rem',
                          color: 'var(--color-secondary)',
                          fontWeight: '600',
                          textDecoration: 'none',
                        }}
                      >
                        View Full Itinerary
                      </a>
                    </div>
                  </div>
                </div>
              );
            }

            if (item.isDinner) {
              const dinnerColor = '#a855f7';
              return (
                <div key={item.id} className="timeline-item">
                  <div className="timeline-marker" style={{ fontSize: '0.8rem', borderColor: dinnerColor }}>
                    🍽️
                  </div>
                  <div className="glass-panel timeline-card" style={{ padding: '12px 18px', display: 'flex', flexDirection: 'column', gap: '6px', borderLeft: `3px solid ${dinnerColor}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: '0.75rem',
                        fontWeight: '700',
                        color: dinnerColor,
                        background: 'rgba(168, 85, 247, 0.08)',
                        padding: '2px 8px',
                        borderRadius: '20px',
                        border: '1px solid rgba(168, 85, 247, 0.2)'
                      }}>
                        Dinner
                      </span>
                      <h3 style={{ margin: 0, fontSize: '1.05rem', fontFamily: 'var(--font-title)', fontWeight: '600' }}>
                        {item.title}
                      </h3>
                    </div>
                    {item.notes && (
                      <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '2px 0 4px 0', lineHeight: '1.5' }}>
                        {item.notes}
                      </p>
                    )}
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                      <span>
                        👨‍🍳 Chefs:{' '}
                        <strong style={{ color: 'var(--color-text-main)' }}>
                          {item.chef
                            ? item.chef.split(',').map(id => getUserName(users, id.trim())).join(', ')
                            : 'Needed'}
                        </strong>
                      </span>
                      <span>
                        🧽 Clean-up:{' '}
                        <strong style={{ color: 'var(--color-text-main)' }}>
                          {item.washer
                            ? item.washer.split(',').map(id => getUserName(users, id.trim())).join(', ')
                            : 'Needed'}
                        </strong>
                      </span>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      borderTop: '1px solid var(--color-glass-border)',
                      paddingTop: '8px',
                      marginTop: '2px'
                    }}>
                      <a
                        href="#dinners"
                        onClick={() => {
                          if (item.dinnerFirestoreId) {
                            sessionStorage.setItem('prine_focus_dinner', item.dinnerFirestoreId);
                          }
                        }}
                        style={{
                          fontSize: '0.75rem',
                          color: dinnerColor,
                          fontWeight: '600',
                          textDecoration: 'none'
                        }}
                      >
                        View Dinner Details
                      </a>
                    </div>
                  </div>
                </div>
              );
            }

            // Resolve display values — linked activity items always use the live activity as source of truth
            const displayTitle = linkedActivity ? (linkedActivity.title || "Untitled Activity") : (item.title || "Untitled");
            const displayEmoji = linkedActivity ? (linkedActivity.emoji || "☀️") : (item.emoji || "📅");
            // Freeform and linked-activity events use primary blue as their accent
            const cardColor = 'var(--color-primary)';
            const cardBg = 'rgba(2, 132, 199, 0.08)';
            const cardBorder = 'rgba(2, 132, 199, 0.15)';

            return (
              <div key={item.id} className="timeline-item">
                <div className="timeline-marker" style={{ fontSize: '0.8rem', borderColor: cardColor }}>
                  {displayEmoji}
                </div>

                <div className="glass-panel timeline-card" style={{ padding: '12px 18px', display: 'flex', flexDirection: 'column', gap: '6px', borderLeft: `3px solid ${cardColor}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: '0.75rem',
                        fontWeight: '700',
                        color: cardColor,
                        background: cardBg,
                        padding: '2px 8px',
                        borderRadius: '20px',
                        border: `1px solid ${cardBorder}`
                      }}>
                        {item.time}
                      </span>
                      <h3 style={{ margin: 0, fontSize: '1.05rem', fontFamily: 'var(--font-title)', fontWeight: '600' }}>
                        {displayTitle}
                      </h3>
                    </div>

                    <div style={{ display: 'inline-flex', gap: '6px' }}>
                      <button
                        onClick={() => handleStartEdit(item)}
                        className="btn-inline-edit"
                        title="Edit Event"
                      >
                        [Edit]
                      </button>
                      {isOwner && (
                        <button
                          onClick={() => handleDeleteEvent(item.id)}
                          className="btn-inline-delete"
                          title="Remove Event"
                        >
                          [Remove]
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Notes: only shown for freeform items; linked activity items have no notes */}
                  {!linkedActivity && item.notes && (
                    <p style={{
                      fontSize: '0.8rem',
                      color: 'var(--color-text-muted)',
                      margin: '2px 0 4px 0',
                      lineHeight: '1.5',
                      wordBreak: 'normal',
                      overflowWrap: 'break-word'
                    }}>
                      {item.notes}
                    </p>
                  )}

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderTop: '1px solid var(--color-glass-border)',
                    paddingTop: '8px',
                    marginTop: '2px',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                      Added by: <strong style={{ color: 'var(--color-text-main)' }}>{getUserName(users, item.createdBy)}</strong>
                    </span>

                    {linkedActivity && (
                      <a
                        href="#activities"
                        onClick={() => {
                          sessionStorage.setItem('prine_focus_activity', item.activityId);
                        }}
                        style={{
                          fontSize: '0.75rem',
                          color: 'var(--color-secondary)',
                          fontWeight: '600',
                          textDecoration: 'none'
                        }}
                      >
                        View Activity Details
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
